import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, FileText } from "lucide-react";

import type { RenderFile } from "./contract";
import type { ArtifactInteractionKind } from "./artifactInteraction";

type ArtifactMachine = {
  id?: string;
  name?: string;
  label?: string;
  status?: string;
};

type ArtifactRecord = {
  machine?: ArtifactMachine;
  type?: string;
  suffix?: string;
  position?: string;
  params?: unknown[];
  input?: unknown;
  output?: unknown;
  schema?: unknown;
  code?: unknown;
  ui?: { primitive?: string } & Record<string, unknown>;
  design?: unknown;
  refs?: unknown[];
  [key: string]: unknown;
};

export type HydrateArtifact = (artifact: unknown, opts?: { force?: boolean }) => Promise<unknown>;

type ArtifactRendererProps = {
  artifact?: unknown;
  render?: RenderFile;
  title: string;
  hydrateArtifact?: HydrateArtifact;
  onHydratedArtifact?: (artifact: unknown) => void;
  onArtifactInteraction?: (interaction: ArtifactInteractionKind, detail?: Record<string, unknown>) => void | Promise<unknown>;
  interactionEnabled?: boolean;
};

export type ArtifactInteractionDetail = Record<string, unknown>;

const INTERACTIVE_CDN_JSDELIVR_REF = "sci//interactive:cdn:@(text:url:'https://cdn.jsdelivr.net/npm')";
const JSDELIVR_NPM_PREFIX = "https://cdn.jsdelivr.net/npm";

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const asArtifact = (value: unknown): ArtifactRecord =>
  isObject(value) ? value as ArtifactRecord : {};

const machineIdOf = (artifact: ArtifactRecord): string =>
  String(artifact.machine?.id || artifact.machine?.name || "");

const isConnectorSurfaceArtifact = (artifact: ArtifactRecord): boolean =>
  machineIdOf(artifact).startsWith("connector_surface_");

const artifactTitleOf = (artifact: ArtifactRecord, fallback: string): string =>
  String(artifact.machine?.label || artifact.machine?.name || fallback);

const artifactRefsOf = (artifact: ArtifactRecord): string[] =>
  Array.isArray(artifact.refs) ? artifact.refs.map(String) : [];

const ARTIFACT_INTERACTIONS = new Set<ArtifactInteractionKind>([
  "clicked",
  "selected",
  "hovered",
  "typed",
  "download",
  "shared",
  "open",
  "opened",
  "submitted",
  "@auth",
  "@prioritized",
  "@scrolled",
  "@navigated",
  "@sorted",
  "@filtered",
  "@searched",
]);

const allowsJsDelivrCdn = (artifact: ArtifactRecord): boolean =>
  artifactRefsOf(artifact).includes(INTERACTIVE_CDN_JSDELIVR_REF);

const isJsDelivrNpmUrl = (src: string): boolean => {
  try {
    const url = new URL(src, window.location.href);
    return url.href.startsWith(JSDELIVR_NPM_PREFIX);
  } catch {
    return false;
  }
};

const valueAt = (value: unknown, path: string): unknown => {
  if (!path || !isObject(value)) return undefined;
  return path.split(".").reduce<unknown>((cur, key) => {
    if (!isObject(cur)) return undefined;
    return cur[key];
  }, value);
};

const payloadObject = (artifact: ArtifactRecord, render?: RenderFile): Record<string, unknown> => {
  const input = isObject(artifact.input) ? artifact.input : {};
  const output = isObject(artifact.output) ? artifact.output : {};
  const renderPayload = isObject(render?.payload) ? render.payload as Record<string, unknown> : {};
  return { ...renderPayload, ...output, ...input };
};

const sanitizeDomInteractionDetail = (detail: unknown): Record<string, unknown> => {
  if (!isObject(detail)) return {};
  const out: Record<string, unknown> = {};
  for (const key of ["column", "query", "page", "path", "rowId", "surfaceId", "mode", "conversationId", "label", "machine"]) {
    const value = detail[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") out[key] = value;
  }
  // THE AGENT-INTENT KEYS (agent wave): the agent/agent-list artifacts' forms
  // emit machine ids and human words — bounded strings (an id/name past the
  // bound is junk, dropped, never truncated into a corrupt id) and one
  // bounded string array for the create form's skill words.
  for (const key of ["agent", "task", "name"]) {
    const value = detail[key];
    if (typeof value === "string" && value.length > 0 && value.length <= 300) out[key] = value;
  }
  if (Array.isArray(detail.skill)) {
    const skill = detail.skill
      .filter((word): word is string => typeof word === "string" && word.length > 0 && word.length <= 120)
      .slice(0, 24);
    if (skill.length) out.skill = skill;
  }
  // A change-mapping interaction stages a ROOT map claim (announcement
  // payload:@map** — a `map` object is required, exactly the oracle claim
  // shape). The claim rides the map lane, never interaction evidence.
  if (isObject(detail.map) && isObject((detail.map as Record<string, unknown>).map)) out.map = detail.map;
  // A data-owning interaction (@filtered) may AUTHOR protocol read atoms —
  // the artifact JS's declared boundary. They rewrite the artifact's OWN
  // input.data.reads world-side (recompose at WRITE); runtime validates the
  // atoms at the crossing, the shell only transports sci-spelled strings.
  if (Array.isArray(detail.reads)) {
    const reads = detail.reads.filter((r): r is string => typeof r === "string" && r.startsWith("sci"));
    if (reads.length) out.reads = reads;
  }
  // TWO DOORS, ONE CROSSING (the wrapper-owned harness pane): the artifact's
  // own invoke input declares its entry lane; the shell only transports the
  // declared word — the world's audit row records the door.
  if (detail.door === "invoke") out.door = "invoke";
  return out;
};

const htmlOf = (artifact: ArtifactRecord, render?: RenderFile): string => {
  const payload = payloadObject(artifact, render);
  const direct = payload.html ?? artifact.input ?? render?.payload;
  return typeof direct === "string" ? direct : typeof payload.body === "string" ? payload.body : "";
};

const textOf = (artifact: ArtifactRecord, render?: RenderFile): string => {
  const payload = payloadObject(artifact, render);
  const direct = payload.text ?? payload.markdown ?? payload.content ?? payload.body ?? render?.payload ?? artifact.input;
  if (typeof direct === "string") return direct;
  if (artifact.suffix === "json" && artifact.input != null) return JSON.stringify(artifact.input, null, 2);
  return "";
};

const rowsOf = (artifact: ArtifactRecord, render?: RenderFile): Record<string, unknown>[] => {
  const payload = payloadObject(artifact, render);
  const candidates = [
    payload.rows,
    payload.items,
    valueAt(artifact.input, "rows"),
    valueAt(artifact.output, "rows"),
    Array.isArray(artifact.params) ? artifact.params : undefined,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.every(isObject)) {
      return candidate as Record<string, unknown>[];
    }
  }
  if (isObject(artifact.schema)) {
    return Object.entries(artifact.schema).map(([field, spec]) => ({
      field,
      type: isObject(spec) ? spec.type || "field" : typeof spec,
      description: isObject(spec) ? spec.description || "" : "",
    }));
  }
  return [];
};

const codeOf = (artifact: ArtifactRecord, render?: RenderFile): { language: string; body: string } => {
  const code = artifact.code;
  if (typeof code === "string") return { language: artifact.suffix || "text", body: code };
  if (isObject(code)) {
    const body = code.body || code.source || code.code;
    if (typeof body === "string") return { language: String(code.language || artifact.suffix || "text"), body };
  }
  return { language: artifact.suffix || "text", body: textOf(artifact, render) };
};

const needsHydration = (artifact: ArtifactRecord): boolean => {
  const machineId = machineIdOf(artifact);
  if (!machineId) return false;
  if (isConnectorSurfaceArtifact(artifact)) return true;
  if (artifact.suffix === "html") {
    // A machine-backed HTML seed is a warm-start hint, not the freshness owner.
    // It may be a registered template (`{{&input}}`) or an older composed body
    // with empty/stale data. Opening must switch immediately and render the
    // loading bridge while the existing download path hydrates the persisted
    // current artifact body, never display the seed shell as truth.
    return true;
  }
  if (artifact.suffix === "svg") {
    const input = isObject(artifact.input) ? artifact.input : {};
    return typeof input.svg !== "string" || !input.svg;
  }
  if (["pdf", "png", "jpg"].includes(String(artifact.suffix || ""))) {
    // a file body (pdf bytes / image url) is needed to render — dehydrated feed rows
    // strip it, so hydrate when absent. ui.primitive (e.g. report) must NOT block this.
    const input = isObject(artifact.input) ? artifact.input : {};
    const payload = payloadObject(artifact);
    return !input.pdf && !input.url && !input.src && !payload.pdf && !payload.url && !payload.src;
  }
  if (["md", "txt", "js", "ts", "py", "json"].includes(String(artifact.suffix || ""))) return !textOf(artifact);
  // presence decides (the artifact:type family is dead): a shell with no
  // input/output/code face has nothing to render yet.
  return artifact.input == null && artifact.output == null && artifact.code == null;
};

const unresolvedStyle: React.CSSProperties = {
  height: "100%",
  display: "grid",
  alignContent: "center",
  gap: 8,
  padding: "28px 24px",
  color: "var(--color-text-secondary, #9ca3af)",
  background: "var(--color-surface, #1b1b1b)",
};

const surfaceStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  minHeight: 0,
  background: "var(--color-surface, #1b1b1b)",
  color: "var(--color-text-primary, #f3f3f3)",
  overflow: "hidden",
};

function ArtifactCanvas({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: 220,
        background: "var(--color-surface, #1b1b1b)",
        color: "var(--color-text-primary, #f3f3f3)",
        overflow: "hidden",
        containerType: "inline-size",
      }}
      data-artifact-canvas="true"
    >
      {children}
    </div>
  );
}

function UnresolvedArtifact({ artifact, reason }: { artifact: ArtifactRecord; reason: string }) {
  return (
    <div style={unresolvedStyle} data-artifact-state="unresolved">
      <strong style={{ color: "var(--color-text-primary, #f3f3f3)", fontSize: 16 }}>Artifact unresolved</strong>
      <span>{reason}</span>
      <code style={{ fontSize: 11, overflowWrap: "anywhere" }}>{machineIdOf(artifact) || "missing machine id"}</code>
    </div>
  );
}

// The ONE artifact loading view — an instant, theme-correct skeleton shown the
// moment a report is opened (download in flight) AND while its html mounts
// hidden, so the transition is one continuous loading state until the finished
// artifact swaps in atomically. Absolute-filling so it overlays a hidden mount.
function ArtifactLoading({ label = "Loading artifact" }: { label?: string }) {
  return (
    <div className="artifact-loading" data-artifact-state="loading">
      <span className="artifact-loading-spinner" aria-hidden="true" />
      <span className="artifact-loading-label">{label}</span>
    </div>
  );
}

function InteractiveHtml({ html, allowJsDelivr }: { html: string; allowJsDelivr: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  // ATOMIC MOUNT (Ori's UX ruling): a click shows a loading view instantly and
  // the artifact swaps in ONLY when the finished view is ready — never a
  // partial/unstyled intermediate. The html mounts HIDDEN (visibility:hidden
  // keeps layout so the wrapper's contain()/inline-SVG/`cqw` container sizing
  // all measure correctly) with the loading overlay on top; it is revealed in a
  // SINGLE frame once the browser has applied the artifact's own <style>, laid
  // out, and the synchronous inline scripts have drawn. This removes the prod
  // "giant unstyled glyph before styles apply" flash.
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const host = ref.current;
    if (!host) return;
    setReady(false);
    host.innerHTML = html;
    const scripts = Array.from(host.querySelectorAll("script"));
    scripts.forEach((script) => {
      const src = script.getAttribute("src");
      if (src && (!allowJsDelivr || !isJsDelivrNpmUrl(src))) {
        script.replaceWith(document.createComment("external script requires sci//interactive:cdn jsdelivr atom"));
        return;
      }
      const next = document.createElement("script");
      for (const attr of Array.from(script.attributes)) next.setAttribute(attr.name, attr.value);
      next.async = false;
      next.textContent = script.textContent || "";
      script.replaceWith(next);
    });
    const htmx = (window as typeof window & { htmx?: { process?: (el: Element) => void } }).htmx;
    htmx?.process?.(host);
    // Reveal after two frames: the first lets the browser apply the just-mounted
    // <style> + lay out, the second lands the reveal on a clean painted frame so
    // the finished view appears at once (never a progressive/unstyled build-up).
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setReady(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      host.innerHTML = "";
    };
  }, [allowJsDelivr, html]);

  return (
    <div style={{ position: "relative", ...surfaceStyle }} data-artifact-surface-frame="interactive-html">
      <div
        ref={ref}
        style={{ width: "100%", height: "100%", minHeight: 0, visibility: ready ? "visible" : "hidden" }}
        data-artifact-surface="interactive-html"
        data-interactive-cdn={allowJsDelivr ? "jsdelivr" : ""}
        data-interactive-ready={ready ? "true" : ""}
      />
      {!ready && <ArtifactLoading label="Loading report" />}
    </div>
  );
}

function DataTable({
  artifact,
  render,
  onArtifactInteraction,
}: {
  artifact: ArtifactRecord;
  render?: RenderFile;
  onArtifactInteraction?: ArtifactRendererProps["onArtifactInteraction"];
}) {
  const [search, setSearch] = useState("");
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [sort, setSort] = useState<string>("");
  const [page, setPage] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const rows = rowsOf(artifact, render);
  const columns = useMemo(() => {
    const explicit = payloadObject(artifact, render).columns;
    if (Array.isArray(explicit)) return explicit.map(String);
    return rows[0] ? Object.keys(rows[0]) : [];
  }, [artifact, render, rows]);
  const artifactId = machineIdOf(artifact);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = q ? rows.filter((row) => JSON.stringify(row).toLowerCase().includes(q)) : rows;
    if (filterValue.trim()) {
      const value = filterValue.trim().toLowerCase();
      filtered = filtered.filter((row) => {
        if (filterColumn) return String(row[filterColumn] ?? "").toLowerCase().includes(value);
        return columns.some((column) => String(row[column] ?? "").toLowerCase().includes(value));
      });
    }
    if (!sort) return filtered;
    return [...filtered].sort((a, b) => String(a[sort] ?? "").localeCompare(String(b[sort] ?? "")));
  }, [columns, filterColumn, filterValue, rows, search, sort]);
  const pageSize = 80;
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = visible.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const rowHeight = 34;
  const viewportHeight = 340;
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 3);
  const renderCount = Math.ceil(viewportHeight / rowHeight) + 7;
  const windowRows = pageRows.slice(start, start + renderCount);

  useEffect(() => {
    setPage(0);
    setScrollTop(0);
  }, [filterColumn, filterValue, search, sort]);

  const emit = (interaction: ArtifactInteractionKind, detail?: Record<string, unknown>) => {
    onArtifactInteraction?.(interaction, { artifact: artifactId, ...detail });
  };

  if (!rows.length) {
    return (
      <div style={{ ...surfaceStyle, padding: 18 }} data-artifact-surface="entity-table" data-artifact-state="shell">
        <div style={{ display: "grid", gap: 8, maxWidth: 540 }}>
          <strong style={{ fontSize: 16 }}>{artifact.machine?.label || "Surface registered"}</strong>
          <span style={{ color: "var(--color-text-secondary, #9ca3af)", lineHeight: 1.45 }}>
            Waiting for the first surface read. This artifact will fill here when data arrives.
          </span>
        </div>
      </div>
    );
  }
  return (
    <div style={{ ...surfaceStyle, padding: 16, overflow: "hidden", display: "grid", gridTemplateRows: "auto auto 1fr auto", gap: 10 }} data-artifact-surface="entity-table">
      <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1fr) minmax(120px, 180px) minmax(120px, 220px)", gap: 8 }}>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            emit("@searched", { query: event.target.value });
          }}
          placeholder="Search rows"
          style={{
            width: "100%",
            minHeight: 34,
            borderRadius: 7,
            border: "1px solid var(--color-border, #2f2f2f)",
            background: "var(--color-bg, #111)",
            color: "inherit",
            padding: "0 10px",
          }}
        />
        <select
          value={filterColumn}
          onChange={(event) => {
            setFilterColumn(event.target.value);
            emit("@filtered", { column: event.target.value, query: filterValue });
          }}
          style={{ minHeight: 34, borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", background: "var(--color-bg, #111)", color: "inherit", padding: "0 10px" }}
        >
          <option value="">Any column</option>
          {columns.map((column) => <option key={column} value={column}>{column}</option>)}
        </select>
        <input
          value={filterValue}
          onChange={(event) => {
            setFilterValue(event.target.value);
            emit("@filtered", { column: filterColumn, query: event.target.value });
          }}
          placeholder="Filter value"
          style={{ minHeight: 34, borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", background: "var(--color-bg, #111)", color: "inherit", padding: "0 10px" }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, color: "var(--color-text-secondary, #9ca3af)", fontSize: 12 }}>
        <span>{visible.length} rows</span>
        <span>Page {safePage + 1} of {pageCount}</span>
      </div>
      <div
        style={{ overflow: "auto", border: "1px solid var(--color-border, #2f2f2f)", borderRadius: 8, minHeight: 0 }}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13 }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "var(--color-surface, #1b1b1b)" }}>
          <tr>
            {columns.map((column) => (
              <th key={column} style={{ textAlign: "left", padding: "9px 8px", borderBottom: "1px solid var(--color-border, #2f2f2f)" }}>
                <button
                  type="button"
                  onClick={() => {
                    const next = sort === column ? "" : column;
                    setSort(next);
                    emit("@sorted", { column: next || column, mode: next ? "ascending" : "none" });
                  }}
                  style={{ color: "inherit", background: "transparent", border: "none", padding: 0, fontWeight: 800, cursor: "pointer" }}
                  data-artifact-interaction="@sorted"
                  data-artifact-column={column}
                >
                  {column}{sort === column ? " ↑" : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
          <tbody style={{ position: "relative", height: pageRows.length * rowHeight }}>
            {start > 0 ? <tr style={{ height: start * rowHeight }}><td colSpan={columns.length} /></tr> : null}
          {windowRows.map((row, rowIndex) => (
            <tr key={start + rowIndex} style={{ height: rowHeight }}>
              {columns.map((column) => (
                <td key={column} style={{ padding: "8px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 72%, transparent)" }}>
                  {String(row[column] ?? "")}
                </td>
              ))}
            </tr>
          ))}
            {start + windowRows.length < pageRows.length ? <tr style={{ height: (pageRows.length - start - windowRows.length) * rowHeight }}><td colSpan={columns.length} /></tr> : null}
        </tbody>
      </table>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          disabled={safePage <= 0}
          onClick={() => {
            const next = Math.max(0, safePage - 1);
            setPage(next);
            emit("@navigated", { page: next + 1 });
          }}
          style={{ minHeight: 34, padding: "0 12px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", background: "var(--color-bg, #111)", color: "inherit" }}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={safePage >= pageCount - 1}
          onClick={() => {
            const next = Math.min(pageCount - 1, safePage + 1);
            setPage(next);
            emit("@navigated", { page: next + 1 });
          }}
          style={{ minHeight: 34, padding: "0 12px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", background: "var(--color-bg, #111)", color: "inherit" }}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function TextSurface({ artifact, render, code = false }: { artifact: ArtifactRecord; render?: RenderFile; code?: boolean }) {
  const source = code ? codeOf(artifact, render).body : textOf(artifact, render);
  if (!source) return <UnresolvedArtifact artifact={artifact} reason="Text artifact body is unavailable." />;
  return (
    <pre
      style={{
        ...surfaceStyle,
        margin: 0,
        padding: 22,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        lineHeight: 1.55,
        fontFamily: code ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : "inherit",
        fontSize: code ? 13 : 15,
      }}
      data-artifact-surface={code ? "code" : "text"}
    >
      {source}
    </pre>
  );
}

function SvgSurface({ artifact, render, title }: { artifact: ArtifactRecord; render?: RenderFile; title: string }) {
  const payload = payloadObject(artifact, render);
  const raw = typeof payload.svg === "string" ? payload.svg : "";
  if (raw) {
    // a generated vector chart (ui:chart × file:svg) carries its svg body inline so it
    // themes via the channel CSS vars; strip scripts (a chart is static geometry).
    const safe = raw.replace(/<script[\s\S]*?<\/script>/gi, "");
    return (
      <div
        style={{ ...surfaceStyle, display: "grid", placeItems: "center", padding: 12 }}
        data-artifact-surface="svg"
        dangerouslySetInnerHTML={{ __html: safe }}
      />
    );
  }
  const src = String(render?.url || payload.url || payload.src || "");
  if (!src) return <UnresolvedArtifact artifact={artifact} reason="SVG artifact has no inline body or source." />;
  return (
    <div style={{ ...surfaceStyle, display: "grid", placeItems: "center", padding: 12 }} data-artifact-surface="svg">
      <img src={src} alt={title} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
    </div>
  );
}

function ImageSurface({ artifact, render, title }: { artifact: ArtifactRecord; render?: RenderFile; title: string }) {
  const payload = payloadObject(artifact, render);
  const pdf = typeof payload.pdf === "string" ? payload.pdf : "";
  const inlinePdf = pdf ? `data:${String(payload.contentType || "application/pdf")};base64,${pdf}` : "";
  const src = String(render?.url || payload.url || payload.src || inlinePdf);
  if (!src) return <UnresolvedArtifact artifact={artifact} reason="File artifact has no downloadable source yet." />;
  if (artifact.suffix === "pdf" || render?.suffix === "pdf") {
    return <object data={src} type="application/pdf" style={surfaceStyle} data-artifact-surface="pdf" />;
  }
  return (
    <div style={{ ...surfaceStyle, display: "grid", placeItems: "center", padding: 12 }} data-artifact-surface="image">
      <img src={src} alt={title} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
    </div>
  );
}

function PresentationSurface({ artifact, render, title }: { artifact: ArtifactRecord; render?: RenderFile; title: string }) {
  // a pptx deck renders its DECLARED slides (input.slides — the deck's own input
  // slot, like the document's blocks); the real file body (input.pptx) stays the
  // downloadable encoding. No slides payload = unresolved, never a blank card.
  const payload = payloadObject(artifact, render);
  const slides = Array.isArray(payload.slides) ? payload.slides.filter(isObject) as Record<string, unknown>[] : [];
  if (!slides.length) return <UnresolvedArtifact artifact={artifact} reason="Presentation artifact has no slides payload." />;
  return (
    <div style={{ ...surfaceStyle, overflow: "auto", padding: 16, display: "grid", gap: 14, alignContent: "start" }} data-artifact-surface="presentation">
      {slides.map((slide, index) => (
        <section
          key={index}
          style={{
            aspectRatio: "16 / 9",
            border: "1px solid var(--color-border, #2f2f2f)",
            borderRadius: 10,
            background: "var(--color-bg, #111)",
            padding: "clamp(14px, 5%, 28px)",
            display: "grid",
            gridTemplateRows: "auto 1fr auto",
            gap: 10,
            minHeight: 0,
            overflow: "hidden",
          }}
          data-presentation-slide={index + 1}
        >
          <strong style={{ fontSize: "clamp(15px, 4.2cqw, 22px)", lineHeight: 1.25 }}>
            {String(slide.title || `Slide ${index + 1}`)}
          </strong>
          <ul style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6, alignContent: "start", overflow: "hidden" }}>
            {(Array.isArray(slide.bullets) ? slide.bullets : []).map((bullet, bulletIndex) => (
              <li key={bulletIndex} style={{ fontSize: "clamp(12px, 3cqw, 15px)", lineHeight: 1.4 }}>{String(bullet)}</li>
            ))}
          </ul>
          <span style={{ justifySelf: "end", fontSize: 11, color: "var(--color-text-secondary, #9ca3af)" }}>
            {title ? `${title} · ` : ""}{index + 1} / {slides.length}
          </span>
        </section>
      ))}
    </div>
  );
}

function IntegrationSurface({ artifact }: { artifact: ArtifactRecord }) {
  const payload = payloadObject(artifact);
  const rows = [
    ["Status", payload.status || artifact.machine?.status],
    ["Instances", Array.isArray(payload.instances) ? payload.instances.length : payload.instances],
    // the body `adapters` slot is DEAD (audit wave): the adapter declarations
    // ride the artifact's refs lane (`integration:adapters:@workflow**:<verb>`)
    // and render in the refs chips below — the world declares, this renders.
  ].filter((row) => row[1] != null && row[1] !== "");
  if (!rows.length && !artifact.refs?.length) return <UnresolvedArtifact artifact={artifact} reason="Integration artifact has no detail payload." />;
  return (
    <div style={{ ...surfaceStyle, padding: 22, overflow: "auto" }} data-artifact-surface="integration">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {rows.map(([label, value]) => (
          <div key={String(label)} style={{ padding: 12, borderRadius: 8, border: "1px solid var(--color-border, #2f2f2f)", background: "var(--color-bg, #111)" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 900, color: "var(--color-text-secondary, #9ca3af)", marginBottom: 7 }}>{String(label)}</div>
            <div style={{ fontWeight: 800 }}>{String(value)}</div>
          </div>
        ))}
      </div>
      {artifact.refs?.length ? (
        <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {artifact.refs.slice(0, 8).map((ref, index) => (
            <span key={index} style={{ padding: "5px 8px", borderRadius: 999, background: "color-mix(in srgb, var(--accent-primary, #6B7EEA) 14%, transparent)", color: "var(--accent-primary, #6B7EEA)", fontSize: 11, fontWeight: 800 }}>
              {String(ref)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function WorkflowSurface({ artifact, render }: { artifact: ArtifactRecord; render?: RenderFile }) {
  const code = codeOf(artifact, render);
  const hasCode = !!code.body;
  const payload = payloadObject(artifact, render);
  return (
    <div style={{ ...surfaceStyle, padding: 22, overflow: "auto" }} data-artifact-surface="workflow">
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {["language", "entrypoint", "address"].map((key) => payload[key] ? (
            <span key={key} style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>
              <strong>{key}</strong> {String(payload[key])}
            </span>
          ) : null)}
        </div>
        {hasCode ? (
          <pre style={{ margin: 0, padding: 14, borderRadius: 8, background: "var(--color-bg, #111)", overflow: "auto", whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", fontSize: 13 }}>
            {code.body}
          </pre>
        ) : (
          <UnresolvedArtifact artifact={artifact} reason="Workflow artifact has no executable metadata body." />
        )}
      </div>
    </div>
  );
}

const inputOf = (artifact: ArtifactRecord): Record<string, unknown> =>
  isObject(artifact.input) ? artifact.input : {};

const arrayRecordsOf = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isObject) as Record<string, unknown>[] : [];

const formatLabel = (value: unknown): string =>
  String(value || "").replace(/[_-]+/g, " ").trim();

const formatNumber = (value: unknown): string => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: Math.abs(n) < 1 ? 2 : 0,
  }).format(n);
};

const confirmationQuestionsOf = (artifact: ArtifactRecord): Record<string, unknown>[] =>
  arrayRecordsOf(inputOf(artifact).questions);

const confirmationReportItemsOf = (artifact: ArtifactRecord): Record<string, unknown>[] =>
  arrayRecordsOf(inputOf(artifact).items);

const confirmationAnswersOf = (artifact: ArtifactRecord): Record<string, unknown>[] =>
  arrayRecordsOf(inputOf(artifact).answers);

const isConfirmationNotificationArtifact = (artifact: ArtifactRecord): boolean => {
  const input = inputOf(artifact);
  // canonical "confirm" (the confirm-loop wave); persisted pre-wave
  // artifacts still spell "confirmation" — read-tolerate.
  return ["confirm", "confirmation"].includes(String(input.level || ""))
    && confirmationQuestionsOf(artifact).length > 0;
};

const isConfirmationAnswerArtifact = (artifact: ArtifactRecord): boolean =>
  !!inputOf(artifact).confirmationId && confirmationAnswersOf(artifact).length > 0;

function ConfirmationRows({
  rows,
  valueKey,
  emptyValue,
}: {
  rows: Record<string, unknown>[];
  valueKey: "proposedValue" | "value";
  emptyValue: string;
}) {
  return (
    <div style={{
      minHeight: 0,
      overflow: "auto",
      border: "1px solid var(--color-border, #2f2f2f)",
      borderRadius: 8,
    }}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
        <thead style={{ position: "sticky", top: 0, background: "var(--color-surface, #1b1b1b)", zIndex: 1 }}>
          <tr>
            {["Period", "Statement", "Line", "Type", "Value"].map((label) => (
              <th key={label} style={{ textAlign: "left", padding: "9px 10px", borderBottom: "1px solid var(--color-border, #2f2f2f)", color: "var(--color-text-secondary, #9ca3af)", fontSize: 10, textTransform: "uppercase" }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const formattedValue = formatNumber(row[valueKey]);
            return (
              <tr key={`${String(row.ambiguityId || row.checkId || index)}:${String(row.period || "")}`}>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 70%, transparent)", fontWeight: 800 }}>{String(row.period || "")}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 70%, transparent)", color: "var(--color-text-secondary, #9ca3af)" }}>{formatLabel(row.statement)}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 70%, transparent)" }}>{String(row.reportLine || row.checkId || "Key value")}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 70%, transparent)", color: "var(--color-text-secondary, #9ca3af)" }}>{formatLabel(row.valueType)}</td>
                <td style={{ padding: "9px 10px", borderBottom: "1px solid color-mix(in srgb, var(--color-border, #2f2f2f) 70%, transparent)", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 900 }}>
                  {formattedValue || emptyValue}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ConfirmationNotificationSurface({ artifact }: { artifact: ArtifactRecord }) {
  const input = inputOf(artifact);
  const questions = confirmationQuestionsOf(artifact);
  const reportItems = confirmationReportItemsOf(artifact);
  const periods = Array.from(new Set(questions.map((row) => String(row.period || "")).filter(Boolean)));
  const statements = Array.from(new Set(questions.map((row) => formatLabel(row.statement)).filter(Boolean)));
  return (
    <div style={{ ...surfaceStyle, padding: 18, overflow: "hidden", display: "grid", gridTemplateRows: "auto auto auto minmax(0, 1fr)", gap: 12 }} data-artifact-surface="confirmation-notification">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <strong style={{ fontSize: 18 }}>{String(input.header || artifact.machine?.label || "Confirm key numbers")}</strong>
          <span style={{ color: "var(--color-text-secondary, #9ca3af)", lineHeight: 1.4 }}>
            {String(input.summary || `${questions.length} values need confirmation`)}
          </span>
        </div>
        <span style={{ padding: "6px 9px", borderRadius: 7, background: "color-mix(in srgb, var(--accent-primary, #6B7EEA) 18%, transparent)", color: "var(--accent-primary, #6B7EEA)", fontWeight: 900, fontSize: 12 }}>
          confirmation
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <span style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>{questions.length} values</span>
        <span style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>{periods.length} periods</span>
        {statements.slice(0, 3).map((statement) => (
          <span key={statement} style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>{statement}</span>
        ))}
      </div>
      {reportItems.length > 0 && (
        <div style={{ display: "grid", gap: 8 }} data-artifact-surface="confirmation-report-links">
          <span style={{ color: "var(--color-text-secondary, #9ca3af)", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0 }}>
            {reportItems.length} linked report{reportItems.length === 1 ? "" : "s"}
          </span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            {reportItems.map((item, index) => (
              <button
                key={`${String(item.conversationId || item.machine || index)}`}
                type="button"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 10,
                  minWidth: 0,
                  padding: "10px 11px",
                  borderRadius: 8,
                  border: "1px solid var(--color-border, #2f2f2f)",
                  background: "color-mix(in srgb, var(--color-surface, #1b1b1b) 88%, transparent)",
                  color: "var(--color-text-primary, #f3f3f3)",
                  textAlign: "left",
                  cursor: "pointer",
                }}
                onClick={(event) => {
                  const detail = {
                    interaction: "open",
                    conversationId: String(item.conversationId || ""),
                    label: String(item.label || item.kind || "Report"),
                    machine: String(item.machine || ""),
                  };
                  event.currentTarget.dispatchEvent(new CustomEvent("causit-artifact-interaction", {
                    bubbles: true,
                    detail,
                  }));
                }}
              >
                <FileText size={15} style={{ color: "var(--accent-primary, #6B7EEA)" }} />
                <span style={{ minWidth: 0, display: "grid", gap: 2 }}>
                  <strong style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                    {String(item.label || item.kind || "Report")}
                  </strong>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--color-text-secondary, #9ca3af)", fontSize: 10, textTransform: "uppercase" }}>
                    {String(item.statement || item.kind || "")}
                  </span>
                </span>
                <ArrowUpRight size={14} style={{ color: "var(--accent-primary, #6B7EEA)" }} />
              </button>
            ))}
          </div>
        </div>
      )}
      <ConfirmationRows rows={questions} valueKey="proposedValue" emptyValue="needs input" />
    </div>
  );
}

function ConfirmationAnswerSurface({ artifact }: { artifact: ArtifactRecord }) {
  const input = inputOf(artifact);
  const answers = confirmationAnswersOf(artifact);
  const periods = Array.from(new Set(answers.map((row) => String(row.period || "")).filter(Boolean)));
  return (
    <div style={{ ...surfaceStyle, padding: 18, overflow: "hidden", display: "grid", gridTemplateRows: "auto auto 1fr", gap: 12 }} data-artifact-surface="confirmation-answer">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
        <div style={{ display: "grid", gap: 6 }}>
          <strong style={{ fontSize: 18 }}>{artifact.machine?.label || "Confirmation answer"}</strong>
          <span style={{ color: "var(--color-text-secondary, #9ca3af)", lineHeight: 1.4 }}>
            {answers.length} confirmed values submitted as artifact evidence.
          </span>
        </div>
        <span style={{ padding: "6px 9px", borderRadius: 7, background: "color-mix(in srgb, #22c55e 18%, transparent)", color: "#6ee7b7", fontWeight: 900, fontSize: 12 }}>
          confirmed
        </span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        <span style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>#{String(input.confirmationId || "")}</span>
        <span style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>{periods.length} periods</span>
        <span style={{ padding: "6px 8px", borderRadius: 7, border: "1px solid var(--color-border, #2f2f2f)", fontSize: 12 }}>human confirmation</span>
      </div>
      <ConfirmationRows rows={answers} valueKey="value" emptyValue="confirmed" />
    </div>
  );
}

function PrimitiveSurface({
  artifact,
  render,
  onArtifactInteraction,
}: {
  artifact: ArtifactRecord;
  render?: RenderFile;
  onArtifactInteraction?: ArtifactRendererProps["onArtifactInteraction"];
}) {
  const primitive = artifact.ui?.primitive || render?.primitive;
  // ONLY genuine React primitives route here. chart/report are CONTENT that crosses a
  // FILE encoding (svg / pdf / interactive html) — they render by the file axis below,
  // never downgraded to a table. ui.primitive is a semantic label, not a render router.
  if (primitive === "table") return <DataTable artifact={artifact} render={render} onArtifactInteraction={onArtifactInteraction} />;
  if (primitive === "icon") {
    const payload = payloadObject(artifact, render);
    return (
      <div style={{ ...surfaceStyle, display: "grid", placeItems: "center", fontSize: 56 }} data-artifact-surface="icon">
        {String(payload.icon || payload.symbol || artifact.machine?.label || "*")}
      </div>
    );
  }
  return null;
}

export function ArtifactRenderer({ artifact: artifactInput, render, title, hydrateArtifact, onHydratedArtifact, onArtifactInteraction, interactionEnabled = true }: ArtifactRendererProps) {
  const seed = useMemo(() => {
    const fromRender = isObject(render?.payload) && Array.isArray((render?.payload as Record<string, unknown>).artifact)
      ? ((render?.payload as Record<string, unknown>).artifact as unknown[])[0]
      : undefined;
    return asArtifact(artifactInput || fromRender || render?.payload);
  }, [artifactInput, render]);
  const [hydrated, setHydrated] = useState<ArtifactRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // a failed hydrate must not kill the box: the world's download door
  // lawfully refuses while an artifact's demand materializes (the continuity
  // rendezvous — a fresh fork's no-input demand rides the whole gate chain),
  // so the serve window opens AFTER the box mounts. Keep pulling while
  // mounted; the next success replaces the error state.
  const [hydrateRetry, setHydrateRetry] = useState(0);
  const machineId = machineIdOf(seed);
  const rootRef = useRef<HTMLDivElement>(null);
  // MOUNT-LIFETIME liveness (the drill re-download): an interaction ack can
  // land tens of seconds after the click, and the interaction effect re-runs
  // on every stream re-render — its local `cancelled` flag is the wrong
  // gate for work that must survive re-renders and die only with the
  // component.
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);
  const hydratedRef = useRef<ArtifactRecord | null>(null);
  useEffect(() => { hydratedRef.current = hydrated; }, [hydrated]);

  useEffect(() => {
    setHydrateRetry(0);
  }, [seed, machineId]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | undefined;
    if (hydrateRetry === 0) {
      setHydrated(null);
      setError("");
    }
    if (!hydrateArtifact || !machineId || !needsHydration(seed)) return;
    setLoading(true);
    hydrateArtifact(seed)
      .then((value) => {
        if (!cancelled) {
          if ((value as { materializing?: boolean } | null)?.materializing) {
            // the world's honest WAITING reply — the artifact's demand is
            // still composing; keep the box loading and re-ask.
            retryTimer = window.setTimeout(() => {
              if (!cancelled) setHydrateRetry((tick) => tick + 1);
            }, 5_000);
            return;
          }
          const next = asArtifact(value);
          setHydrated(next);
          setError("");
          onHydratedArtifact?.(next);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          retryTimer = window.setTimeout(() => {
            if (!cancelled) setHydrateRetry((tick) => tick + 1);
          }, 5_000);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateArtifact, machineId, onHydratedArtifact, seed, hydrateRetry]);

  const artifact = hydrated || seed;
  const suffix = String(artifact.suffix || render?.suffix || "");
  // THE ARTIFACT IS THE CROSSING (Ori's draft, 2026-07-26): the dispatch reads
  // the artifact's ONE DECLARED kind (sci//artifact:kind:@workflow = HEADLESS —
  // backend only, no interface), never a presence sniff over code/execute/refs.
  // The integration definition shell stays its own declared gate ref.
  const refs = Array.isArray(artifact.refs) ? artifact.refs.map(String) : [];
  const isIntegrationShell = refs.some((r) => r.includes("integration:instance:@instance**"));
  const isWorkflowShell = refs.includes("sci//artifact:kind:@workflow");
  const primitive = artifact.ui?.primitive || render?.primitive;
  const label = artifactTitleOf(artifact, title);
  const allowJsDelivr = allowsJsDelivrCdn(artifact);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const fromElement = (target: EventTarget | null) => {
      if (!interactionEnabled) return;
      const element = target instanceof Element ? target.closest("[data-artifact-interaction]") : null;
      const raw = element?.getAttribute("data-artifact-interaction") || "";
      if (!ARTIFACT_INTERACTIONS.has(raw as ArtifactInteractionKind)) return;
      onArtifactInteraction?.(raw as ArtifactInteractionKind, {
        artifact: machineId,
        column: element?.getAttribute("data-artifact-column") || undefined,
        path: element?.getAttribute("data-artifact-path") || undefined,
      });
    };
    const onClick = (event: MouseEvent) => fromElement(event.target);
    const onCustom = (event: Event) => {
      const rawDetail = (event as CustomEvent).detail;
      const raw = isObject(rawDetail) ? String(rawDetail.interaction || "") : "";
      // The world-owned wrapper's Refresh strip: re-download the persisted
      // body (calc-per-write serves it fresh) and re-render — no reads echo,
      // no polling; the world no longer rewrites reads on interaction.
      if (raw === "refresh") {
        if (!hydrateArtifact) return;
        // the EXPLICIT Refresh affordance forces the one download past the
        // content-addressed cache (the user asked for the world's current body).
        // Liveness gates on the MOUNT ref, never this effect's `cancelled` —
        // the same law the drill pull learned: `cancelled` flips on every
        // stream re-render, and a paged Book drain outlives one render, so
        // gating on it silently DISCARDED the downloaded body (the refreshed
        // bytes landed in the cache while the glass kept the stale view,
        // 2026-08-09).
        void hydrateArtifact(seed, { force: true })
          .then((value) => {
            if (aliveRef.current) setHydrated(asArtifact(value));
          })
          .catch((err) => {
            if (aliveRef.current) setError(err instanceof Error ? err.message : String(err));
          });
        return;
      }
      const detail = sanitizeDomInteractionDetail(rawDetail);
      if (interactionEnabled && ARTIFACT_INTERACTIONS.has(raw as ArtifactInteractionKind)) {
        const outcome = onArtifactInteraction?.(raw as ArtifactInteractionKind, {
          artifact: machineId,
          ...detail,
        });
        void outcome;
        // A READS-CARRYING interaction (the cause drill) REWRITES the
        // artifact's own declaration and the world recomposes INSIDE the
        // crossing — so the recomposed body must be PULLED, on the same
        // bounded schedule the Upgrade affordance owns (the ack alone is not
        // a signal: a loaded stack can time the outlet's world push out while
        // the world still completes, and gating the re-download on that ack
        // left the glass on "Asking the book…" forever, 2026-08-08). Each
        // pull force-downloads and stops the moment the served
        // content-address moves past the clicked body; liveness gates on the
        // MOUNT ref, never this effect's `cancelled` (it flips on every
        // stream re-render).
        const declaredReads = Array.isArray((detail as { reads?: unknown[] }).reads)
          ? ((detail as { reads?: unknown[] }).reads as unknown[]).length
          : 0;
        if (declaredReads > 0 && hydrateArtifact) {
          const contentOf = (value: unknown): string => String(
            (value as { shape?: { content?: unknown } } | null)?.shape?.content || "");
          const baseline = contentOf(hydratedRef.current ?? seed);
          const pullDelays = [3_000, 25_000, 70_000, 160_000];
          const pull = (attempt: number) => {
            if (!aliveRef.current || attempt >= pullDelays.length) return;
            window.setTimeout(() => {
              if (!aliveRef.current) return;
              hydrateArtifact(seed, { force: true })
                .then((value) => {
                  if (!aliveRef.current) return;
                  const next = asArtifact(value);
                  if (contentOf(next) && contentOf(next) !== baseline) {
                    setHydrated(next);
                    return;
                  }
                  pull(attempt + 1);
                })
                .catch((err) => {
                  console.warn("[artifact-drill] recompose pull failed — retrying on the bounded schedule:", err);
                  pull(attempt + 1);
                });
            }, pullDelays[attempt]);
          };
          pull(0);
        }
      }
    };
    root.addEventListener("click", onClick, true);
    root.addEventListener("causit-artifact-interaction", onCustom);
    return () => {
      root.removeEventListener("click", onClick, true);
      root.removeEventListener("causit-artifact-interaction", onCustom);
    };
    // `loading`/`error` are in the deps because the component early-returns
    // those views WITHOUT rootRef — the effect must re-run when the real
    // surface mounts, else a box that hydrated (dehydrated feed face →
    // loading first) never gets its interaction listener and every in-artifact
    // form submit dies silently (the task-queue ADD regression, 2026-07-24).
  }, [machineId, onArtifactInteraction, hydrateArtifact, seed, interactionEnabled, loading, error]);

  if (!machineId && !render) {
    return (
      <ArtifactCanvas>
        <UnresolvedArtifact artifact={artifact} reason="Artifact machine is required." />
      </ArtifactCanvas>
    );
  }
  if (loading) {
    return (
      <ArtifactCanvas>
        <ArtifactLoading label={label ? `Loading ${label}` : "Loading artifact"} />
      </ArtifactCanvas>
    );
  }
  if (error) {
    return (
      <ArtifactCanvas>
        <UnresolvedArtifact artifact={artifact} reason={error} />
      </ArtifactCanvas>
    );
  }

  let surface: React.ReactNode;
  // ONLY genuine React primitives mount here. chart/report are CONTENT that renders by
  // its FILE encoding (html→InteractiveHtml, pdf→ImageSurface) — gating on table/icon
  // (not truthiness of the JSX element, which is ALWAYS truthy) lets them fall through.
  if (isConfirmationNotificationArtifact(artifact)) {
    surface = <ConfirmationNotificationSurface artifact={artifact} />;
  }
  if (!surface && isConfirmationAnswerArtifact(artifact)) {
    surface = <ConfirmationAnswerSurface artifact={artifact} />;
  }
  if (!surface && (primitive === "table" || primitive === "icon")) {
    surface = <PrimitiveSurface artifact={artifact} render={render} onArtifactInteraction={onArtifactInteraction} />;
  }
  if (!surface && (suffix === "html" || artifact.position === "interactive")) {
    const html = htmlOf(artifact, render);
    surface = html ? <InteractiveHtml html={html} allowJsDelivr={allowJsDelivr} /> : <UnresolvedArtifact artifact={artifact} reason="Interactive artifact has no HTML body." />;
  }
  // Entity and workflow-surface interfaces both serve world-composed HTML
  // above. The grid remains the FILE-axis render for rowcol-position kinds.
  if (!surface && (suffix === "csv" || suffix === "xlsx" || artifact.position === "rowcol")) {
    surface = <DataTable artifact={artifact} render={render} onArtifactInteraction={onArtifactInteraction} />;
  }
  if (!surface && isIntegrationShell) surface = <IntegrationSurface artifact={artifact} />;
  if (!surface && isWorkflowShell) surface = <WorkflowSurface artifact={artifact} render={render} />;
  if (!surface && suffix === "svg") surface = <SvgSurface artifact={artifact} render={render} title={label} />;
  if (!surface && ["png", "jpg", "pdf"].includes(suffix)) surface = <ImageSurface artifact={artifact} render={render} title={label} />;
  if (!surface && suffix === "pptx") surface = <PresentationSurface artifact={artifact} render={render} title={label} />;
  if (!surface && ["js", "ts", "py", "json"].includes(suffix)) surface = <TextSurface artifact={artifact} render={render} code />;
  if (!surface && ["md", "txt"].includes(suffix)) surface = <TextSurface artifact={artifact} render={render} />;
  surface = surface || <UnresolvedArtifact artifact={artifact} reason="Artifact contract is incomplete for rendering." />;

  return <div ref={rootRef} data-artifact-render-root={machineId || undefined} style={{ minHeight: 0, height: "100%" }}><ArtifactCanvas>{surface}</ArtifactCanvas></div>;
}
